import { type FC, type KeyboardEvent, type ReactNode, useLayoutEffect } from "react";
import { cn } from "../../lib/utils";
import { ACTIVE_CELL_RING, useDataGridCellNav } from "./useDataGridCellNav";

type DataGridSelectableCellProps = {
	rowId: string;
	colId: string;
	children?: ReactNode;
	className?: string;
};

export const DataGridSelectableCell: FC<DataGridSelectableCellProps> = ({
	rowId,
	colId,
	children,
	className = "",
}) => {
	const {
		cellRef,
		isActive,
		isFocusable,
		scrollMarginClass,
		handleFocus,
		handleNavKey,
		handleCopyKey,
	} = useDataGridCellNav(rowId, colId);

	useLayoutEffect(() => {
		if (isActive && cellRef.current && document.activeElement !== cellRef.current) {
			cellRef.current.focus({ preventScroll: true });
			cellRef.current.scrollIntoView({ block: "nearest", inline: "nearest" });
		}
	}, [isActive, cellRef]);

	const handleCellKey = (e: KeyboardEvent<HTMLDivElement>) => {
		if (handleCopyKey(e)) return;
		if (handleNavKey(e)) return;
	};

	return (
		<div
			ref={cellRef}
			data-col-id={colId}
			tabIndex={isFocusable ? 0 : -1}
			onClick={handleFocus}
			onFocus={handleFocus}
			onKeyDown={isFocusable ? handleCellKey : undefined}
			style={isActive ? ACTIVE_CELL_RING : undefined}
			className={cn(
				"flex items-center h-full focus:outline-none cursor-default",
				isActive && "bg-surface-light",
				scrollMarginClass,
				className,
			)}
		>
			{children}
		</div>
	);
};
